import { Upload } from 'lucide-react'
import * as React from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

export interface FileUploadProps {
  mode?: 'basic' | 'advanced'
  auto?: boolean
  chooseLabel?: string
  name: string
  disabled?: boolean
  accept?: string
  maxFileSize?: number
  url: string
  onBeforeSend?: (event: { xhr: XMLHttpRequest; formData: FormData }) => void | Promise<void>
  onError?: (event: { xhr: XMLHttpRequest }) => void
  onUpload?: (event: { xhr: XMLHttpRequest }) => void
  className?: string
}

const FileUpload = React.forwardRef<HTMLInputElement, FileUploadProps>(
  (
    {
      mode = 'basic',
      auto = false,
      chooseLabel = 'Choose',
      name,
      disabled = false,
      accept,
      maxFileSize,
      url,
      onBeforeSend,
      onError,
      onUpload,
      className,
    },
    ref
  ) => {
    const inputRef = React.useRef<HTMLInputElement>(null)
    const [uploading, setUploading] = React.useState(false)

    React.useImperativeHandle(ref, () => inputRef.current!)

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files
      if (!files || files.length === 0) return

      const file = files[0]

      // Check file size
      if (maxFileSize && file.size > maxFileSize) {
        console.error(`File size exceeds maximum of ${maxFileSize} bytes`)
        onError?.({ xhr: new XMLHttpRequest() })
        return
      }

      if (auto) {
        await uploadFile(file)
      }
    }

    const uploadFile = async (file: File) => {
      setUploading(true)

      const formData = new FormData()
      formData.append(name, file)

      const xhr = new XMLHttpRequest()

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100
          console.log(`Upload progress: ${percentComplete}%`)
        }
      })

      xhr.addEventListener('load', () => {
        setUploading(false)
        if (xhr.status >= 200 && xhr.status < 300) {
          onUpload?.({ xhr })
        } else {
          onError?.({ xhr })
        }
        // Reset input
        if (inputRef.current) {
          inputRef.current.value = ''
        }
      })

      xhr.addEventListener('error', () => {
        setUploading(false)
        onError?.({ xhr })
        if (inputRef.current) {
          inputRef.current.value = ''
        }
      })

      xhr.addEventListener('abort', () => {
        setUploading(false)
        if (inputRef.current) {
          inputRef.current.value = ''
        }
      })

      xhr.open('POST', url)

      // Call onBeforeSend to allow setting headers
      await onBeforeSend?.({ xhr, formData })

      xhr.send(formData)
    }

    if (mode === 'basic') {
      return (
        <div className={cn('flex items-center gap-2', className)}>
          <Input
            ref={inputRef}
            type="file"
            accept={accept}
            disabled={disabled || uploading}
            onChange={handleFileChange}
            className="hidden"
            id={`file-upload-${name}`}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => inputRef.current?.click()}
            disabled={disabled || uploading}
            className="w-full"
          >
            <Upload className="mr-2 h-4 w-4" />
            {uploading ? 'Uploading...' : chooseLabel}
          </Button>
        </div>
      )
    }

    // Advanced mode could be implemented later if needed
    return null
  }
)

FileUpload.displayName = 'FileUpload'

export { FileUpload }
